/**
 * Extracts the trailing running number from a task code.
 *
 * @param code - Task code in the format `PREFIX-RUNNING_NUMBER` (e.g. `PRE-1`, `PRE-16-1`).
 * @returns The parsed running number, or `0` if extraction fails.
 */
const getRunningNumber = (code: string) => {
  const runningNumber = code.split('-').pop();
  return runningNumber ? parseInt(runningNumber) : 0;
};

/**
 * Comparator function for sorting task codes by their trailing running number.
 *
 * @param codeA - First task code to compare (e.g. `PRE-1`).
 * @param codeB - Second task code to compare (e.g. `PRE-2`).
 * @param order - Sort direction: `'asc'` (default) for ascending, `'desc'` for descending.
 * @returns A negative number if `codeA` should come first, positive if `codeB` should, or `0` if equal.
 *
 * @example
 * const codes = ['PRE-3', 'PRE-1', 'PRE-2'];
 * codes.sort((a, b) => sortByTaskCode(a, b));
 * // => ['PRE-1', 'PRE-2', 'PRE-3']
 *
 * @example
 * const codes = ['PRE-16-3', 'PRE-16-1', 'PRE-16-2'];
 * codes.sort((a, b) => sortByTaskCode(a, b));
 * // => ['PRE-16-1', 'PRE-16-2', 'PRE-16-3']
 */
export const sortByTaskCode = (codeA: string, codeB: string, order: 'asc' | 'desc' = 'asc') => {
  const result = getRunningNumber(codeA) - getRunningNumber(codeB);
  return order === 'asc' ? result : -result;
};
